/**
 * Defensive dedup + formatting for the `actor_name` field on
 * `user_notifications`. When the backend rolls up multiple events into
 * one notification it writes `actor_name` as a comma-separated list
 * (e.g. "Alice, Bob, Alice") — duplicates leak through when two
 * separate writes target the same recipient and a backfill stitches
 * them together. Web previously rendered the raw string; iOS dedupes
 * before display.
 *
 * Returns `null` for empty/whitespace-only input so callers can omit
 * the actor span entirely.
 */
export function formatActorName(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const piece of raw.split(",")) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(trimmed);
  }
  if (ordered.length === 0) return null;
  if (ordered.length === 1) return ordered[0];
  if (ordered.length === 2) return `${ordered[0]} and ${ordered[1]}`;
  return `${ordered[0]} and ${ordered.length - 1} others`;
}
